import React from "react";
import { Helmet } from "react-helmet";
import SignUpForm from "./SignUpForm.js";
import { CssBaseline, Grid, Paper, ThemeProvider } from "@mui/material";
import PerksList from "./PerksList.js";
import useCustomTheme from "../../hooks/useCustomTheme.js";
import Item from "../../components/item.js";

const SignInSide = () => {
  const theme = useCustomTheme();
  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>Sign Up and Create a Setster Account</title>
        <meta
          name="description"
          content="Join Setster today to streamline your scheduling process. Create a new account in just a few clicks and enjoy comprehensive appointment management tools."
        />
        <meta name="author" content="Setster" />
        <meta content="yes" name="apple-mobile-web-app-capable" />
        <meta
          property="og:title"
          content="Sign Up and Create a Setster Account"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.setster.com" />
        <meta property="og:site_name" content="Setster" />
        <meta
          property="og:description"
          content="Join Setster today to streamline your scheduling process. Create a new account in just a few clicks and enjoy comprehensive appointment management tools."
        />
        <meta property="og:author" content="Setster" />
        <meta
          property="og:image"
          content="https://www.setster.com/landing-page/images/setster-logo.png"
        />
        <meta property="og:image:width" content="280" />
        <meta property="og:image:height" content="90" />
        <meta
          name="twitter:title"
          content="Sign Up and Create a Setster Account"
        />
        <meta
          name="twitter:description"
          content="Join Setster today to streamline your scheduling process. Create a new account in just a few clicks and enjoy comprehensive appointment management tools."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Setster" />
        <meta name="twitter:url" content="https://www.setster.com" />
        <meta
          name="twitter:image"
          content="https://www.setster.com/landing-page/images/setster-logo.png"
        />
        <meta name="twitter:image:width" content="280" />
        <meta name="twitter:image:height" content="90" />
        <link rel="alternate" href="https://www.setster.com/signup" />
        <link rel="canonical" href="https://www.setster.com/signup" />
      </Helmet>

      <Grid
        container
        direction="row"
        component="main"
        sx={{
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2000 1500"><rect fill="%23081634" width="2000" height="1500"/><defs><rect stroke="%23081634" stroke-width="0.1" width="1" height="1" id="s" rx=".3"/><pattern id="a" width="3" height="3" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><use fill="%23081634" href="%23s" y="2"/><use fill="%23081634" href="%23s" x="1" y="2"/><use fill="%23081634" href="%23s" x="2" y="2"/><use fill="%23081634" href="%23s"/><use fill="%23081634" href="%23s" x="2"/><use fill="%23494b58" href="%23s" x="1" y="1"/></pattern><pattern id="b" width="7" height="11" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23494b58"><use href="%23s"/><use href="%23s" y="5"/><use href="%23s" x="1" y="10"/><use href="%23s" x="2" y="1"/><use href="%23s" x="2" y="4"/><use href="%23s" x="3" y="8"/><use href="%23s" x="4" y="3"/><use href="%23s" x="4" y="7"/><use href="%23s" x="5" y="2"/><use href="%23s" x="5" y="6"/><use href="%23s" x="6" y="9"/></g></pattern><pattern id="h" width="5" height="13" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23494b58"><use href="%23s" y="5"/><use href="%23s" y="8"/><use href="%23s" x="1" y="1"/><use href="%23s" x="1" y="9"/><use href="%23s" x="1" y="12"/><use href="%23s" x="2"/><use href="%23s" x="2" y="4"/><use href="%23s" x="3" y="2"/><use href="%23s" x="3" y="6"/><use href="%23s" x="3" y="11"/><use href="%23s" x="4" y="3"/><use href="%23s" x="4" y="7"/><use href="%23s" x="4" y="10"/></g></pattern><pattern id="c" width="17" height="13" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23081634"><use href="%23s" y="11"/><use href="%23s" x="2" y="9"/><use href="%23s" x="5" y="12"/><use href="%23s" x="9" y="4"/><use href="%23s" x="12" y="1"/><use href="%23s" x="16" y="6"/></g></pattern><pattern id="d" width="19" height="17" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23081634"><use href="%23s" y="9"/><use href="%23s" x="16" y="5"/><use href="%23s" x="14" y="2"/><use href="%23s" x="11" y="11"/><use href="%23s" x="6" y="14"/></g><g fill="%23595b65"><use href="%23s" x="3" y="13"/><use href="%23s" x="9" y="7"/><use href="%23s" x="13" y="10"/><use href="%23s" x="15" y="4"/><use href="%23s" x="18" y="1"/></g></pattern><pattern id="e" width="47" height="53" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23D0021B"><use href="%23s" x="2" y="5"/><use href="%23s" x="16" y="38"/><use href="%23s" x="46" y="42"/><use href="%23s" x="29" y="20"/></g></pattern><pattern id="f" width="59" height="71" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%23635BFF"><use href="%23s" x="33" y="13"/><use href="%23s" x="27" y="54"/><use href="%23s" x="55" y="55"/></g></pattern><pattern id="g" width="139" height="97" patternUnits="userSpaceOnUse" patternTransform="scale(20.3) translate(-950.74 -713.05)"><g fill="%2300CD84"><use href="%23s" x="11" y="8"/><use href="%23s" x="51" y="13"/><use href="%23s" x="17" y="73"/><use href="%23s" x="99" y="57"/></g></pattern></defs><rect fill="url(%23a)" width="100%" height="100%"/><rect fill="url(%23b)" width="100%" height="100%"/><rect fill="url(%23h)" width="100%" height="100%"/><rect fill="url(%23c)" width="100%" height="100%"/><rect fill="url(%23d)" width="100%" height="100%"/><rect fill="url(%23e)" width="100%" height="100%"/><rect fill="url(%23f)" width="100%" height="100%"/><rect fill="url(%23g)" width="100%" height="100%"/></svg>')`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100vh",
        }}
        justifyContent="center"
        alignItems="center"
      >
        <CssBaseline />
        <Grid
          container
          sx={{
            margin: theme.spacing(2),
            maxWidth: "fit-content",
            width: {
              xs: "100%",
              sm: "100%",
              md: "100%",
              lg: "100%",
              xl: "95%",
              xxl: "80%",
              xxxl: "70%",
            },
          }}
        >
          <Grid
            md={6}
            lg={6}
            xl={6}
            sx={{
              display: {
                xs: "none",
                sm: "none",
                md: "block",
                lg: "block",
                xl: "block",
              },
              paddingLeft: {
                xs: "0px",
                sm: "0px",
                md: theme.spacing(3),
                lg: theme.spacing(20),
                xl: theme.spacing(40),
              },
            }}
            item
          >
            <Item
              elevation={0}
              sx={{
                height: "100%",
                backgroundColor: "#E3E6EA",
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ height: "100%", padding: theme.spacing(8) }}
              >
                <PerksList />
              </Grid>
            </Item>
          </Grid>
          <Grid
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
            sx={{
              paddingRight: {
                xs: "0px",
                sm: "0px",
                md: theme.spacing(3),
                lg: theme.spacing(20),
                xl: theme.spacing(40),
              },
              display: {
                md: "block",
                lg: "block",
                xl: "block",
              },
            }}
            alignItems="center"
            justifyContent="flex-start"
            item
          >
            <Item
              elevation={0}
              sx={{
                height: "100%",
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ height: "100%" }}
              >
                <SignUpForm />
              </Grid>
            </Item>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default SignInSide;
